<template>
    <div class="voice-agent-channel-widget">
        <md-button
            ref="buttonRef"
            style="min-width: 20px; max-width: 25px"
            :class="{ [getHealthStatus()]: true }"
            class="actionButtonBreadcrumb m-0 voice-agent-btn"
            @mouseenter="voiceIconHovered = true"
            @mouseleave="voiceIconHovered = false"
            @click="toggleDropdown"
        >
            <VoiceAgentIcon 
                class="voice-agent-icon" 
                :color="voiceIconHovered || isDropdownVisible ? (voiceAgentChannel ? '#4ecdc4' : '#888') : '#3A3F48'"
            />
        </md-button>

        <!-- Custom Dropdown -->
        <div v-if="isDropdownVisible" class="custom-dropdown" :style="dropdownStyle" :class="{ 'dropdown-ready': dropdownReady }">
            <div class="dropdown-content">
                <!-- Agent List -->
                    <div v-if="agents.length === 0" class="no-agents">
                        <p>{{ $t("message.voiceAgentNoAgentsFound") }}</p>
                    <button class="primary-btn" @click="goToAgentsPage">{{ $t("message.voiceAgentCreateAgent") }}</button>
                    </div>
                <div v-else class="agents-list">
                    <div v-for="agent in agents" :key="agent.agent_id" class="agent-item" @click="goToAgentDetail(agent.agent_id)">
                        <div class="agent-info">
                            <div class="agent-icon-wrapper">
                            <i class="material-icons agent-voice-icon">record_voice_over</i>
                            </div>
                            <div class="agent-name">{{ agent.agent_name }}</div>
                        </div>
                        <div class="agent-actions" @click.stop>
                            <button class="action-icon-btn call-btn" @click="startCall(agent)" :title="$t('message.voiceAgentCallTooltip')">
                                    <i class="material-icons">call</i>
                            </button>
                            <button class="action-icon-btn view-btn" @click="goToAgentDetail(agent.agent_id)" :title="$t('message.voiceAgentViewDetailsTooltip')">
                                    <i class="material-icons">visibility</i>
                            </button>
                        </div>
                    </div>
                </div>
                
                <!-- Add New Agent Button -->
                <div class="add-agent" @click="goToAgentsPage">
                    <i class="material-icons">add</i>
                    <span>{{ $t("message.voiceAgentAddNew") }}</span>
                </div>
            </div>
        </div>

        <!-- Backdrop to close dropdown -->
        <div v-if="isDropdownVisible" class="dropdown-backdrop" @click="closeDropdown"></div>

        <!-- Gizli AiVoiceAgentCallStarter komponenti -->
        <div style="position: absolute">
            <AiVoiceAgentCallStarter
                ref="voiceCallStarter"
                :default-agent-id="selectedAgentId"
                :hide-button="true"
                auto-open
                v-if="voiceAgentChannel && selectedAgentId"
                @close="selectedAgentId = null"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { VOICE_AGENT_API } from "../../api/voiceAgent";
import AiVoiceAgentCallStarter from "../AiVoiceAgentCallStarter.vue";
import VoiceAgentIcon from "../svg/appBreadCrumb/VoiceAgentIcon.vue";

export default {
    name: "VoiceAgentChannelStatusWidget",
    components: {
        AiVoiceAgentCallStarter,
        VoiceAgentIcon,
    },
    data() {
        return {
            agents: [],
            loading: false,
            selectedAgentId: null,
            voiceIconHovered: false,
            isDropdownVisible: false,
            dropdownStyle: {},
            dropdownReady: false,
        };
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels,
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
        voiceAgentChannel() {
            return this.channels.find((channel) => channel.type === "retell" && channel.enabled);
        },
    },
    methods: {
        updateDropdownPosition() {
            if (!this.$refs.buttonRef) return;
            
            const buttonRect = this.$refs.buttonRef.$el.getBoundingClientRect();
            const dropdownWidth = 240;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const padding = 16;
            
            let top = buttonRect.bottom + 8;
            let left = buttonRect.left + (buttonRect.width / 2);
            let transform = 'translateX(-50%)';
            
            const wouldOverflowRight = left + (dropdownWidth / 2) > viewportWidth - padding;
            const wouldOverflowLeft = left - (dropdownWidth / 2) < padding;
            
            if (wouldOverflowRight) {
                this.dropdownStyle = {
                    top: `${top}px`,
                    right: `${padding}px`,
                    left: 'auto',
                    transform: 'none',
                };
            } else if (wouldOverflowLeft) {
                this.dropdownStyle = {
                    top: `${top}px`,
                    left: `${padding}px`,
                    right: 'auto',
                    transform: 'none',
                };
            } else {
                this.dropdownStyle = {
                    top: `${top}px`,
                    left: `${left}px`,
                    transform: 'translateX(-50%)',
                };
            }
            
            this.$nextTick(() => {
                const dropdown = this.$el.querySelector('.custom-dropdown');
                if (dropdown) {
                    const dropdownRect = dropdown.getBoundingClientRect();
                    if (dropdownRect.bottom > viewportHeight - padding) {
                        this.dropdownStyle.top = `${buttonRect.top - dropdownRect.height - 8}px`;
                    }
                }
            });
        },
        toggleDropdown() {
            this.isDropdownVisible = !this.isDropdownVisible;
            if (this.isDropdownVisible) {
                this.dropdownReady = false;
                this.$nextTick(() => {
                    this.updateDropdownPosition();
                    setTimeout(() => {
                        this.dropdownReady = true;
                    }, 10);
                });
            } else {
                this.dropdownReady = false;
            }
        },
        closeDropdown() {
            this.isDropdownVisible = false;
            this.dropdownReady = false;
        },
        getHealthStatus() {
            // Ses ajanı kanalının durumunu kontrol et
            return this.voiceAgentChannel ? "healthy" : "not-healthy";
        },

        async fetchAgents() {
            if (!this.voiceAgentChannel) return;

            try {
                this.loading = true;
                const agents = await VOICE_AGENT_API.agents.list(true, true);
                this.agents = agents;
            } catch (error) {
                console.error("Ses ajanları yüklenirken hata:", error);
            } finally {
                this.loading = false;
            }
        },

        goToAgentsPage() {
            this.closeDropdown();
            if (this.voiceAgentChannel) {
                this.$router.push(`/integrations/voice-agent/${this.voiceAgentChannel._id}/dashboard?tab=agents`);
            } else {
                this.$router.push("/integrations/voice-agent");
            }
        },

        goToAgentDetail(agentId) {
            this.closeDropdown();
            if (this.voiceAgentChannel) {
                this.$router.push({
                    path: `/integrations/voice-agent/${this.voiceAgentChannel._id}/dashboard`,
                    query: {
                        tab: "agents",
                        agent_id: agentId,
                        editor_tab: "agent",
                    },
                });
            }
        },

        startCall(agent) {
            // Seçili ajanı ayarla
            this.selectedAgentId = agent.agent_id;
            this.closeDropdown();

            // Arama modalını göster
            this.$nextTick(() => {
                if (this.$refs.voiceCallStarter) {
                    this.$refs.voiceCallStarter.showModal();
                }
            });
        },
    },
    mounted() {
        this.fetchAgents();
    },
};
</script>

<style lang="scss" scoped>
.voice-agent-channel-widget {
    position: relative;
    display: inline-block;
}

.voice-agent-btn {
    background: none !important;
    box-shadow: none !important;
    border: none !important;

    &:hover,
    &:focus,
    &:active {
        background: none !important;
        box-shadow: none !important;
        border: none !important;
    }

    &:before {
        display: none !important;
    }

    .md-ripple {
        display: none !important;
    }

    &.partially-healthy {
        border: 1px solid #faa566 !important;
        animation: pulse 2s infinite;
    }
}

.voice-agent-icon {
    width: 18px !important;
    height: 18px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    
    svg {
        width: 18px !important;
        height: 18px !important;
    }
}

// Custom Dropdown Styles
.custom-dropdown {
    position: fixed;
    background: #F5F6FA;
    border-radius: 8px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
    z-index: 10000;
    min-width: 220px;
    max-width: 260px;
    overflow: hidden;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease-out, visibility 0.15s ease-out;
    
    &.dropdown-ready {
        opacity: 1;
        visibility: visible;
    }

    &::before {
        content: '';
        position: absolute;
        top: -5px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-bottom: 6px solid #F5F6FA;
        filter: drop-shadow(0 -2px 3px rgba(0, 0, 0, 0.05));
    }

    .dropdown-content {
        display: flex;
        flex-direction: column;
        max-height: 240px;
        overflow-y: auto;
        padding: 3px;
        pointer-events: auto;

        &::-webkit-scrollbar {
            width: 3px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
            border-radius: 10px;
        }

        &::-webkit-scrollbar-thumb {
            background: #C9BBF6;
            border-radius: 10px;

            &:hover {
                background: #B8A5F0;
            }
        }
    }
}

.dropdown-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
    background: transparent;
    pointer-events: auto;
}

@media (max-width: 768px) {
    .custom-dropdown {
        right: 8px !important;
        left: auto !important;
        transform: none !important;
    }
}

// No Agents State
.no-agents {
        display: flex;
        flex-direction: column;
        align-items: center;
    padding: 16px 12px;
    gap: 8px;
        text-align: center;

        p {
        margin: 0;
        color: #6B7280;
        font-size: 12px;
        font-weight: 500;
    }

    .primary-btn {
        background: #1890FF;
        color: white;
        border: none;
        padding: 4px 12px;
        border-radius: 5px;
        font-size: 11px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
            background: #0070DD;
        }
    }
}

// Agents List
.agents-list {
    display: flex;
    flex-direction: column;
    }

    .agent-item {
        display: flex;
        justify-content: space-between;
    align-items: center;
    padding: 6px 8px;
    background: #FFFFFF;
    border-radius: 6px;
    margin-bottom: 3px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:last-child {
        margin-bottom: 0;
    }

        &:hover {
        background: #F8F9FA;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .agent-info {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1;
        min-width: 0;

        .agent-icon-wrapper {
            width: 26px;
            height: 26px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 6px;
            flex-shrink: 0;

            .agent-voice-icon {
                color: white !important;
                font-size: 16px;
                -webkit-text-fill-color: white;
            }
            }

            .agent-name {
            font-size: 12px;
                font-weight: 500;
            color: #1F2937;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        }

        .agent-actions {
        display: flex;
        gap: 3px;
        flex-shrink: 0;

        .action-icon-btn {
            width: 22px;
            height: 22px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: none;
            background: transparent;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.2s ease;
            padding: 0;

            .material-icons {
                font-size: 15px;
                color: #6B7280;
            }

            &:hover {
                background: #F3F4F6;

                .material-icons {
                    color: #4B5563;
                }
                }

            &.call-btn:hover .material-icons {
                color: #10B981;
                }

            &.view-btn:hover .material-icons {
                color: #3B82F6;
            }
        }
    }
}

// Add Agent Button
.add-agent {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px;
    cursor: pointer;
    color: #1890FF;
    font-size: 12px;
    font-weight: 500;
    border-top: 1px solid #E5E7EB;
    transition: all 0.2s ease;
    background: #FFFFFF;
    border-radius: 0 0 8px 8px;

    .material-icons {
        font-size: 16px;
    }

    &:hover {
        background: #EFF6FF;
    }
}
</style>
